/**
 * Per-pair hit-stop (lane C0-HIT-REACT, duel audit 2026-10-11 #8): a landed blow freezes the attacker AND the target
 * together on the contact frame, for a time by the blow's class; nobody else stops (no scene-wide freeze, so the other
 * fighters' inputs stay readable). The target shivers a pixel while it is held. Picture only: the World runs on.
 *
 * Times: the class's `HIT_WEIGHT.classes[cls].hitStop` (data/hitWeight.ts, ns-balance's numbers), never under the audit's
 * floor — light 0.035 s, medium 0.06, heavy 0.10, a guard shattered 0.14, a counter 0.12 — until the data carries them.
 * The target's reaction (render/hitReact.ts) starts when its hold ends, so the hold never eats the snap.
 */
import { HIT_WEIGHT, hitWeightOf, type WorldEvent } from '@nine/shared';
import { noteShot } from './lastShot';

/** The audit's floor per class and for the special outcomes (s). */
const FLOOR = { guard: 0, light: 0.035, medium: 0.06, heavy: 0.1, shatter: 0.14, counter: 0.12 } as const;

/** How long a blow holds attacker and target (s); 0 for a dodge. */
export function hitStopOf(skillId: string, outcome: string): number {
  if (outcome === 'guardBreak' || outcome === 'guardShatter') return Math.max(HIT_WEIGHT.classes.heavy.hitStop, FLOOR.shatter);
  if (outcome === 'countered') return FLOOR.counter;
  const hw = hitWeightOf(skillId, outcome as Extract<WorldEvent, { type: 'hit' }>['outcome']);
  if (!hw) return 0;
  return Math.max(hw.hitStop, FLOOR[hw.cls]);
}

interface Hold {
  from: number;
  to: number;
  /** The target shivers; the attacker holds still. */
  shiver: boolean;
  shot: { dx: number } | null;
}

const holds = new Map<number, Hold>();

/** Starts the hold on both sides of a landed blow. Called from effects.handle for every `hit` event. */
export function hitStopOnHit(ev: Extract<WorldEvent, { type: 'hit' }>, time: number): void {
  const d = hitStopOf(ev.skillId, ev.outcome);
  if (d <= 0) return;
  holds.set(ev.tgtId, { from: time, to: time + d, shiver: true, shot: null });
  if (ev.srcId !== ev.tgtId && ev.srcId >= 0) {
    const h = holds.get(ev.srcId);
    // An attacker landing several blows at once (an area move) keeps one hold, the longest.
    if (!h || h.to < time) holds.set(ev.srcId, { from: time, to: time + d, shiver: false, shot: null });
    else h.to = Math.max(h.to, time + d);
  }
  if (holds.size > 256) for (const [id, h] of holds) if (h.to < time) holds.delete(id);
}

/** Holds a pair for exactly `d` s from `time`, over any hold a blow set (the long moves' per-beat `stop`, render/longMove.ts). */
export function hitStopHold(srcId: number, tgtId: number, time: number, d: number): void {
  if (d <= 0) return;
  holds.set(tgtId, { from: time, to: time + d, shiver: true, shot: null });
  if (srcId !== tgtId && srcId >= 0) holds.set(srcId, { from: time, to: time + d, shiver: false, shot: null });
}

/**
 * The shot to draw for entity `id` at world time `t`: while held, the frame drawn at the start of the hold (the target
 * shivering a pixel either side); otherwise `draw()`.
 */
export function hitStopShot<S extends { dx: number }>(id: number, t: number, paint: () => S): S {
  // Every frame drawn is noted for the afterimages (render/lastShot.ts, lane QINGGONG-POSES).
  const draw = (): S => {
    const s = paint();
    noteShot(id, s as { img?: HTMLCanvasElement; facing?: number }, t);
    return s;
  };
  const h = holds.get(id);
  if (!h) return draw();
  if (t >= h.to || t < h.from) {
    holds.delete(id);
    return draw();
  }
  const s = (h.shot ??= draw()) as S;
  if (!h.shiver) return s;
  return { ...s, dx: s.dx + (Math.floor((t - h.from) * 60) % 2 ? 1 : -1) };
}
